import type { ReactNode } from "react";
import { flushSync } from "react-dom";
import { CheckCircleIcon, InfoIcon, WarningCircleIcon, XIcon } from "@phosphor-icons/react";
import { Button as AriaButton, Text, UNSTABLE_Toast as Toast, UNSTABLE_ToastContent as ToastContent, UNSTABLE_ToastQueue as ToastQueue, UNSTABLE_ToastRegion as ToastRegion } from "react-aria-components";
import { cx } from "../cx.ts";

export interface ToastData {
  title: string;
  description?: string;
  tone?: "neutral" | "success" | "danger";
  action?: { label: string; onAction: () => void };
}

/** Eén wachtrij voor de hele app. Animatie via view transitions waar beschikbaar. */
export const toastQueue = new ToastQueue<ToastData>({
  wrapUpdate(fn) {
    const doc = document as Document & { startViewTransition?: (cb: () => void) => unknown };
    if (doc.startViewTransition && !matchMedia("(prefers-reduced-motion: reduce)").matches) doc.startViewTransition(() => flushSync(fn));
    else fn();
  },
});

export function toast(data: ToastData, timeout = data.action ? 8000 : 5000): string {
  return toastQueue.add(data, { timeout });
}

const ICONS: Record<NonNullable<ToastData["tone"]>, ReactNode> = {
  neutral: <InfoIcon className="ui-toast__icon" aria-hidden weight="fill" />,
  success: <CheckCircleIcon className="ui-toast__icon" aria-hidden weight="fill" />,
  danger: <WarningCircleIcon className="ui-toast__icon" aria-hidden weight="fill" />,
};

export function Toaster({ closeLabel, regionLabel }: { closeLabel: string; regionLabel: string }) {
  return (
    <ToastRegion queue={toastQueue} className="ui-toast-region" aria-label={regionLabel}>
      {({ toast: t }) => (
        <Toast toast={t} className={cx("ui-toast", `ui-toast--${t.content.tone ?? "neutral"}`)} style={{ viewTransitionName: t.key }}>
          {ICONS[t.content.tone ?? "neutral"]}
          <ToastContent className="ui-toast__content">
            <Text slot="title" className="ui-toast__title">
              {t.content.title}
            </Text>
            {t.content.description ? (
              <Text slot="description" className="ui-toast__desc">
                {t.content.description}
              </Text>
            ) : null}
          </ToastContent>
          {t.content.action ? (
            <AriaButton
              className="ui-button ui-button--ghost ui-button--sm"
              onPress={() => {
                t.content.action!.onAction();
                toastQueue.close(t.key);
              }}
            >
              {t.content.action.label}
            </AriaButton>
          ) : null}
          <AriaButton slot="close" className="ui-button ui-button--ghost ui-button--sm ui-button--icon ui-toast__close" aria-label={closeLabel}>
            {t.timeout ? <span className="ui-toast__timer" aria-hidden style={{ ["--toast-duration" as string]: `${t.timeout}ms` }} /> : null}
            <XIcon aria-hidden />
          </AriaButton>
        </Toast>
      )}
    </ToastRegion>
  );
}
